 import { useState } from "react";
 import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
 import { Button } from "@/components/ui/button";
 import { Input } from "@/components/ui/input";
 import { Label } from "@/components/ui/label";
 import { useAuth } from "@/contexts/AuthContext";
 import { useToast } from "@/hooks/use-toast";
 import { Loader2, Mail, Lock, User, ArrowLeft } from "lucide-react";
 
 interface AuthModalProps {
   open: boolean;
   onOpenChange: (open: boolean) => void;
 }
 
 type AuthView = "sign-in" | "sign-up" | "verify-email";
 
 const AuthModal = ({ open, onOpenChange }: AuthModalProps) => {
   const [view, setView] = useState<AuthView>("sign-in");
   const [email, setEmail] = useState("");
   const [password, setPassword] = useState("");
   const [name, setName] = useState("");
   const [isLoading, setIsLoading] = useState(false);
   const { signIn, signUp } = useAuth();
   const { toast } = useToast();
 
   const resetForm = () => {
     setEmail("");
     setPassword("");
     setName("");
     setView("sign-in");
   };
 
   const handleClose = (open: boolean) => {
     if (!open) resetForm();
     onOpenChange(open);
   };
 
   const handleSignIn = async (e: React.FormEvent) => {
     e.preventDefault();
     if (!email || !password) {
       toast({ title: "Please fill in all fields", variant: "destructive" });
       return;
     }
 
     setIsLoading(true);
     const { error } = await signIn(email, password);
     setIsLoading(false);
 
     if (error) {
       toast({ 
         title: "Sign in failed", 
         description: error.message || "Invalid email or password",
         variant: "destructive" 
       });
     } else {
       toast({ title: "Welcome back!", description: "You've signed in successfully" });
       handleClose(false);
     }
   };
 
   const handleSignUp = async (e: React.FormEvent) => {
     e.preventDefault();
     if (!email || !password) {
       toast({ title: "Please fill in all fields", variant: "destructive" });
       return;
     }
 
     if (password.length < 6) {
       toast({ title: "Password must be at least 6 characters", variant: "destructive" });
       return;
     }
 
     setIsLoading(true);
     const { error } = await signUp(email, password);
     setIsLoading(false);
 
     if (error) {
       toast({ 
         title: "Sign up failed", 
         description: error.message || "Could not create account",
         variant: "destructive" 
       });
     } else {
       setView("verify-email");
     }
   };
 
   return (
     <Dialog open={open} onOpenChange={handleClose}>
       <DialogContent className="sm:max-w-md">
         <DialogHeader>
           <DialogTitle className="text-center font-heading text-2xl">
             {view === "sign-in" && "Welcome Back"}
             {view === "sign-up" && "Create Account"}
             {view === "verify-email" && "Check Your Email"}
           </DialogTitle>
         </DialogHeader>
 
         {view === "verify-email" ? (
           <div className="text-center py-6 space-y-4">
             <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
               <Mail className="w-8 h-8 text-primary" />
             </div>
             <div>
               <p className="text-muted-foreground">
                 We've sent a verification link to
               </p>
               <p className="font-medium mt-1">{email}</p>
             </div>
             <p className="text-sm text-muted-foreground">
               Please check your inbox and click the link to activate your account.
             </p>
             <Button
               variant="outline"
               onClick={() => setView("sign-in")}
               className="mt-4"
             >
               <ArrowLeft className="w-4 h-4 mr-2" />
               Back to Sign In
             </Button>
           </div>
         ) : (
           <form onSubmit={view === "sign-in" ? handleSignIn : handleSignUp} className="space-y-4">
             {view === "sign-up" && (
               <div className="space-y-2">
                 <Label htmlFor="name">Name (optional)</Label>
                 <div className="relative">
                   <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                   <Input
                     id="name"
                     type="text"
                     placeholder="Your name"
                     value={name}
                     onChange={(e) => setName(e.target.value)}
                     className="pl-10"
                   />
                 </div>
               </div>
             )}
 
             <div className="space-y-2">
               <Label htmlFor="email">Email</Label>
               <div className="relative">
                 <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                 <Input
                   id="email"
                   type="email"
                   placeholder="you@example.com"
                   value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   className="pl-10"
                   required
                 />
               </div>
             </div>
 
             <div className="space-y-2">
               <Label htmlFor="password">Password</Label>
               <div className="relative">
                 <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                 <Input
                   id="password"
                   type="password"
                   placeholder="••••••••"
                   value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   className="pl-10"
                   required
                   minLength={6}
                 />
               </div>
             </div>
 
             <Button
               type="submit"
               variant="sizzle"
               className="w-full"
               disabled={isLoading}
             >
               {isLoading ? (
                 <>
                   <Loader2 className="w-4 h-4 animate-spin mr-2" />
                   {view === "sign-in" ? "Signing in..." : "Creating account..."}
                 </>
               ) : (
                 view === "sign-in" ? "Sign In" : "Create Account"
               )}
             </Button>
 
             <div className="text-center text-sm">
               {view === "sign-in" ? (
                 <p className="text-muted-foreground">
                   Don't have an account?{" "}
                   <button
                     type="button"
                     onClick={() => setView("sign-up")}
                     className="text-primary hover:underline font-medium"
                   >
                     Sign up
                   </button>
                 </p>
               ) : (
                 <p className="text-muted-foreground">
                   Already have an account?{" "}
                   <button
                     type="button"
                     onClick={() => setView("sign-in")}
                     className="text-primary hover:underline font-medium"
                   >
                     Sign in
                   </button>
                 </p>
               )}
             </div>
           </form>
         )}
       </DialogContent>
     </Dialog>
   );
 };
 
 export default AuthModal;